/**
 * Componente de sección de estadísticas por producto con SectionBox
 * Product statistics section component with SectionBox
 */
import { SectionBox } from '@shared/components/ui/SectionBox';
import { TotalResults } from '@shared/components/ui/TotalResults';
import { InfoTooltip } from '@shared/components/ui/InfoTooltip';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { ProductStatisticsTable } from './ProductStatisticsTable';
import { ProductStatistics } from '../../models/ProductStatistics';
import { FiltersSection } from '../interaction';
import { ColumnDef } from '@tanstack/react-table';

interface ProductStatisticsSectionProps {
  data: ProductStatistics[];
  total: number;
  handleSearch: (value: string) => void;
  columns: ColumnDef<any>[];
  selectedColumns: string[];
  onColumnsChange: (newSelected: string[]) => void;
  searchTerm: string;
  isFetching?: boolean;
}

export const ProductStatisticsSection = ({
  data,
  total,
  handleSearch,
  columns,
  selectedColumns,
  onColumnsChange,
  searchTerm,
  isFetching = false,
}: ProductStatisticsSectionProps) => {
  const { t } = useTranslation('logisticsModule');

  // Detectar si hay filtros activos (solo búsqueda)
  // Detect if there are active filters (only search)
  const hasActiveFilters = useMemo(() => {
    return searchTerm && searchTerm.trim().length > 0;
  }, [searchTerm]);

  return (
    <SectionBox data-testid="product-statistics-section">
      {/* Header con título e icono de información - Header with title and info tooltip */}
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-lg font-semibold text-title">{t('product_statistics.title')}</h2>
        <InfoTooltip
          text={t('product_statistics.tooltip')}
          dataTestId="product-statistics-info"
          iconClassName="w-4 h-4"
        />
      </div>

      {/* Barra de controles - Controls bar */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        {/* Controles izquierda - Left controls */}
        <div className="flex items-center gap-3 flex-wrap">
          <FiltersSection
            handleSearch={handleSearch}
            columns={columns}
            selectedColumns={selectedColumns}
            onColumnsChange={onColumnsChange}
            searchTerm={searchTerm}
          />
        </div>
        {/* Total de resultados - Total results */}
        <TotalResults total={total} />
      </div>

      {/* Estado vacío - Empty state (solo cuando no está cargando ni fetching y no hay datos) */}
      {/* Empty state (only when not loading, not fetching, and no data) */}
      {!isFetching && data.length === 0 && (
        <EmptyView type={hasActiveFilters ? 'noResults' : 'emptyState'} icon={EMPTY_VIEW_ICONS.PRODUCTS} />
      )}

      {/* Tabla de estadísticas - Statistics table (mostrar si hay datos o si está fetching para mantenerla visible durante búsquedas) */}
      {/* Statistics table (show if there's data or if fetching to keep it visible during searches) */}
      {(data.length > 0 || isFetching) && (
        <ProductStatisticsTable data={data} isFetching={isFetching} selectedColumns={selectedColumns} />
      )}
    </SectionBox>
  );
};
